/** Console password rules (Profilim → Şifre). Supabase Auth hashes with bcrypt, which reads at most 72 bytes. */
export const PASSWORD_MIN = 10;
export const PASSWORD_MAX = 72;

export type PasswordIssue = "required" | "tooShort" | "tooLong" | "sameAsEmail" | "repeatRequired" | "mismatch";
export type PasswordStrength = "weak" | "fair" | "strong";

/** UTF-8 length: the bcrypt limit is in bytes, so "ş" counts twice. */
const byteLength = (s: string) => new TextEncoder().encode(s).length;

/** Issue with the new password itself, or null. */
export function passwordIssue(password: string, email: string): PasswordIssue | null {
  if (!password) return "required";
  if (password.length < PASSWORD_MIN) return "tooShort";
  if (password.length > PASSWORD_MAX || byteLength(password) > PASSWORD_MAX) return "tooLong";
  const mail = email.trim().toLowerCase();
  if (mail && password.trim().toLowerCase() === mail) return "sameAsEmail";
  return null;
}

/** Issue with the repeated password, or null. */
export function repeatIssue(password: string, repeat: string): PasswordIssue | null {
  if (!repeat) return "repeatRequired";
  return repeat === password ? null : "mismatch";
}

/** Rough strength hint from length and character variety (no dictionary; a hint, not a rule). */
export function passwordStrength(password: string): PasswordStrength {
  const classes = [/[a-zçğıöşü]/, /[A-ZÇĞİÖŞÜ]/, /\d/, /[^A-Za-z0-9çğıöşüÇĞİÖŞÜ]/].filter((r) => r.test(password)).length;
  const distinct = new Set(password).size;
  if (password.length < PASSWORD_MIN || distinct < 5) return "weak";
  // Long passphrases count even with few character types.
  if ((password.length >= 14 && classes >= 3) || (password.length >= 16 && classes >= 2) || password.length >= 24) return "strong";
  if (password.length >= 18) return "fair";
  if (classes <= 1) return "weak";
  if (password.length >= 12 && classes >= 2) return "fair";
  return classes >= 3 ? "fair" : "weak";
}
